import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Button,
  Checkbox,
  Input,
  RichTextEditor,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  absolutizeReplicatedImageSrcs,
  relativizeReplicatedImageSrcs,
  REPLICATED_IMAGE_PATH,
  type RpButton,
  type RpLinkTarget,
  type RpMediaRef,
} from '@mlm/shared';
import { API_BASE_URL, replicatedImageUrl, uploadReplicatedImage } from '../../lib/api';
import { RepeatableRows, type RowField } from '../repeatable-rows';
import { RP_VIDEO_EMBED_HINT, type RpFieldSpec, type RpItemFieldSpec } from './field-specs';

/**
 * The inspector's typed controls. Every control here edits a known shape — this
 * is a typed editor end to end, never a JSON textarea (project convention).
 */

// ── Editor context ──────────────────────────────────────────────────────────

export type RpEditorCtx = {
  /** Sections on the page, for the "jump to section" link target. */
  anchors: { id: string; label: string }[];
  /** Other campaign pages, for the "another page" link target. */
  pages: { slug: string; title: string }[];
  /** Activation kits, for the "package" link target. */
  kits: { id: string; name: string }[];
  /** Virtual products / services, for the "product" link target — the thing a
   *  campaign page sells directly rather than routing through signup. */
  virtualProducts: { id: string; name: string }[];
  /** What THIS page says it sells (`replicated_pages.cta_product_id`), if
   *  anything. Used to flag a button still pointing at the registration form on
   *  a page that has a product — the state every page template starts in, since
   *  every template button is seeded `{kind:'signup'}`. */
  pageCtaProduct?: { id: string; name: string } | null;
};

const Ctx = createContext<RpEditorCtx>({ anchors: [], pages: [], kits: [], virtualProducts: [] });
export const RpEditorProvider = Ctx.Provider;
export const useRpEditorCtx = () => useContext(Ctx);

// ── Layout helpers ──────────────────────────────────────────────────────────

export function RpFieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p> : null}
    </div>
  );
}

// ── Image ───────────────────────────────────────────────────────────────────

/** Upload → store an R2 key; preview through the public by-key read route. */
export function RpImageKeyField({
  value,
  onChange,
}: {
  value: string;
  onChange: (key: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [local, setLocal] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const src = local ?? (value ? replicatedImageUrl(value) : null);

  async function pick(file: File) {
    setLocal(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { key } = await uploadReplicatedImage(file);
      onChange(key);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {src ? (
        <img src={src} alt="" className="h-12 w-20 rounded border bg-white object-cover" />
      ) : (
        <div className="h-12 w-20 rounded border bg-muted" />
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
      </Button>
      {value && !uploading ? (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-destructive"
          onClick={() => {
            setLocal(null);
            onChange('');
          }}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
}

/** An image OR a video URL — the two things a media slot can hold. */
export function RpMediaField({
  value,
  onChange,
}: {
  value: RpMediaRef | undefined;
  onChange: (next: RpMediaRef) => void;
}) {
  // A legacy per-block image reads as "image" and is replaced by a normal
  // upload the moment the admin picks a new one.
  const isVideo = value?.kind === 'video';
  return (
    <div className="space-y-2">
      <SegmentedControl
        size="sm"
        value={isVideo ? 'video' : 'image'}
        onChange={(v) =>
          onChange(v === 'video' ? { kind: 'video', url: '' } : { kind: 'image', imageKey: '' })
        }
        options={[
          { value: 'image', label: 'Image' },
          { value: 'video', label: 'Video' },
        ]}
        ariaLabel="Media type"
      />
      {isVideo ? (
        <div className="space-y-1">
          <Input
            className="h-8"
            value={value?.kind === 'video' ? value.url : ''}
            placeholder="YouTube, Vimeo or an .mp4 link"
            onChange={(e) => onChange({ kind: 'video', url: e.target.value })}
          />
          {/* Same copy the standalone Video section shows, but only while the
              Video tab is active — it would be noise under an image picker. */}
          <p className="text-[11px] leading-snug text-muted-foreground/80">{RP_VIDEO_EMBED_HINT}</p>
        </div>
      ) : value?.kind === 'legacyImage' ? (
        <div className="space-y-1">
          <img
            src={`${API_BASE_URL}/replicated-page/blocks/${value.blockId}/image`}
            alt=""
            className="h-12 w-20 rounded border bg-white object-cover"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({ kind: 'image', imageKey: '' })}
          >
            Replace image
          </Button>
        </div>
      ) : (
        <RpImageKeyField
          value={value?.kind === 'image' ? value.imageKey : ''}
          onChange={(key) => onChange({ kind: 'image', imageKey: key })}
        />
      )}
    </div>
  );
}

// ── Links ───────────────────────────────────────────────────────────────────

const LINK_KINDS = [
  { value: 'signup', label: 'Sign up' },
  { value: 'kit', label: 'Package' },
  { value: 'product', label: 'Product' },
  { value: 'anchor', label: 'Section' },
  { value: 'page', label: 'Page' },
  { value: 'url', label: 'URL' },
  { value: 'none', label: 'None' },
] as const;

/**
 * Where a button goes.
 *
 * Replaces the pre-builder raw `href` box. Two things that box could not do:
 * a mid-page button could never point at signup while preserving the sponsor's
 * referral code (the destination was derived only for the one auto-appended
 * CTA), and it happily accepted `javascript:` on a public page. Both are fixed
 * by making the target a closed set of choices.
 */
export function RpLinkField({
  value,
  onChange,
}: {
  value: RpLinkTarget | undefined;
  onChange: (next: RpLinkTarget) => void;
}) {
  const { anchors, pages, kits, virtualProducts, pageCtaProduct } = useRpEditorCtx();
  const kind = value?.kind ?? 'none';
  return (
    <div className="space-y-2">
      <SegmentedControl
        size="sm"
        value={kind}
        onChange={(k) => {
          if (k === 'signup') onChange({ kind: 'signup' });
          else if (k === 'none') onChange({ kind: 'none' });
          else if (k === 'url') onChange({ kind: 'url', url: '' });
          else if (k === 'anchor') onChange({ kind: 'anchor', sectionId: anchors[0]?.id ?? '' });
          else if (k === 'page') onChange({ kind: 'page', slug: pages[0]?.slug ?? '' });
          else if (k === 'product') onChange({ kind: 'product', productId: '' });
          else onChange({ kind: 'kit', productId: '' });
        }}
        options={LINK_KINDS}
        ariaLabel="Link target"
      />

      {kind === 'signup' ? (
        pageCtaProduct ? (
          // The page declares a product but this button still walks the visitor
          // to the registration form, which ends on the dashboard. Every page
          // template seeds its buttons as `signup`, so this is the DEFAULT state
          // of a templated page that sells something — worth saying out loud
          // rather than leaving to be noticed.
          <p className="text-[11px] text-muted-foreground">
            This page sells <span className="font-medium">{pageCtaProduct.name}</span>, but this
            button goes to the registration form — a buyer ends up on their dashboard.{' '}
            <button
              type="button"
              className="text-primary underline"
              onClick={() => onChange({ kind: 'product', productId: pageCtaProduct.id })}
            >
              Sell {pageCtaProduct.name} instead
            </button>
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            Goes to sign-up, carrying the sponsor&apos;s referral code and this campaign.
          </p>
        )
      ) : null}

      {kind === 'url' ? (
        <Input
          className="h-8"
          value={value?.kind === 'url' ? value.url : ''}
          placeholder="https://…"
          onChange={(e) => onChange({ kind: 'url', url: e.target.value })}
        />
      ) : null}

      {kind === 'anchor' ? (
        anchors.length ? (
          <Select
            value={value?.kind === 'anchor' ? value.sectionId : ''}
            onValueChange={(v) => onChange({ kind: 'anchor', sectionId: v })}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Pick a section" />
            </SelectTrigger>
            <SelectContent>
              {anchors.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-[11px] text-muted-foreground">Add another section to link to first.</p>
        )
      ) : null}

      {kind === 'page' ? (
        pages.length ? (
          <Select
            value={value?.kind === 'page' ? value.slug : ''}
            onValueChange={(v) => onChange({ kind: 'page', slug: v })}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Pick a page" />
            </SelectTrigger>
            <SelectContent>
              {pages.map((p) => (
                <SelectItem key={p.slug} value={p.slug}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-[11px] text-muted-foreground">No other pages yet.</p>
        )
      ) : null}

      {kind === 'kit' ? (
        kits.length ? (
          <>
            <Select
              value={value?.kind === 'kit' ? value.productId : ''}
              onValueChange={(v) => onChange({ kind: 'kit', productId: v })}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Pick a package" />
              </SelectTrigger>
              <SelectContent>
                {kits.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Goes to sign-up with this package chosen, sponsor code intact.
            </p>
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground">No activation kits yet.</p>
        )
      ) : null}

      {kind === 'product' ? (
        virtualProducts.length ? (
          <>
            <Select
              value={value?.kind === 'product' ? value.productId : ''}
              onValueChange={(v) => onChange({ kind: 'product', productId: v })}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Pick a product" />
              </SelectTrigger>
              <SelectContent>
                {virtualProducts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Goes straight to checkout for this product — a visitor who isn&apos;t signed in
              registers first (sponsor code intact) and is brought back to pay. Make sure the
              product is sold to non-activated members, or a new lead can&apos;t buy it.
            </p>
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            No virtual products yet — add one under Virtual Products.
          </p>
        )
      ) : null}
    </div>
  );
}

/** Label + destination + look, for one button. */
export function RpButtonField({
  value,
  optional,
  onChange,
}: {
  value: RpButton | undefined;
  optional?: boolean;
  onChange: (next: RpButton | undefined) => void;
}) {
  if (!value) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onChange({ label: 'Sign up', link: { kind: 'signup' }, variant: 'primary', size: 'md' })
        }
      >
        <Plus className="h-3.5 w-3.5" /> Add button
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <Input
          className="h-8"
          value={value.label}
          placeholder="Button text"
          onChange={(e) => onChange({ ...value, label: e.target.value })}
        />
        {optional ? (
          <button
            type="button"
            aria-label="Remove button"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onChange(undefined)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <RpLinkField value={value.link} onChange={(link) => onChange({ ...value, link })} />
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          size="sm"
          value={value.variant ?? 'primary'}
          onChange={(v) => onChange({ ...value, variant: v })}
          options={[
            { value: 'primary', label: 'Solid' },
            { value: 'outline', label: 'Outline' },
            { value: 'ghost', label: 'Text' },
            { value: 'light', label: 'Light' },
          ]}
          ariaLabel="Button style"
        />
        <SegmentedControl
          size="sm"
          value={value.size ?? 'md'}
          onChange={(v) => onChange({ ...value, size: v })}
          options={[
            { value: 'sm', label: 'S' },
            { value: 'md', label: 'M' },
            { value: 'lg', label: 'L' },
          ]}
          ariaLabel="Button size"
        />
      </div>
    </div>
  );
}

/** The `buttonGroup` section's list of buttons. */
export function RpButtonsField({
  value,
  max,
  onChange,
}: {
  value: RpButton[];
  max: number;
  onChange: (next: RpButton[]) => void;
}) {
  return (
    <div className="space-y-2">
      {value.map((b, i) => (
        <div key={i} className="flex items-start gap-1">
          <div className="flex-1">
            <RpButtonField
              value={b}
              optional
              onChange={(next) =>
                onChange(
                  next
                    ? value.map((x, j) => (j === i ? next : x))
                    : value.filter((_, j) => j !== i),
                )
              }
            />
          </div>
          <div className="flex flex-col gap-0.5 pt-2">
            <button
              type="button"
              aria-label="Move up"
              disabled={i === 0}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              onClick={() => {
                const next = [...value];
                [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                onChange(next);
              }}
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={i === value.length - 1}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              onClick={() => {
                const next = [...value];
                [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
                onChange(next);
              }}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      {value.length < max ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([
              ...value,
              { label: 'Learn more', link: { kind: 'none' }, variant: 'outline', size: 'md' },
            ])
          }
        >
          <Plus className="h-3.5 w-3.5" /> Add button
        </Button>
      ) : null}
    </div>
  );
}

// ── Lists ───────────────────────────────────────────────────────────────────

/** A plain `string[]` (hero bullets, feature points). */
export function RpStringsField({
  value,
  max,
  placeholder,
  onChange,
}: {
  value: string[];
  max: number;
  placeholder?: string;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      {value.map((s, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input
            className="h-8"
            value={s}
            placeholder={placeholder}
            onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <button
            type="button"
            aria-label="Remove"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {value.length < max ? (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, ''])}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      ) : null}
    </div>
  );
}

/** Map an item field spec onto a `RepeatableRows` field. */
function toRowField(f: RpItemFieldSpec): RowField {
  if (f.kind === 'imageKey') return { kind: 'image', key: f.key, label: f.label };
  if (f.kind === 'icon') return { kind: 'icon', key: f.key, label: f.label };
  if (f.kind === 'textarea')
    return { kind: 'textarea', key: f.key, label: f.label, rows: 2, placeholder: f.placeholder };
  return { kind: 'text', key: f.key, label: f.label, placeholder: f.placeholder };
}

/** An object list (benefit cards, steps, quotes) — reuses the storefront editor's row component. */
export function RpItemsField({
  value,
  spec,
  onChange,
}: {
  value: Record<string, unknown>[];
  spec: readonly RpItemFieldSpec[];
  onChange: (next: Record<string, unknown>[]) => void;
}) {
  return (
    <RepeatableRows
      items={value}
      fields={spec.map(toRowField)}
      makeBlank={() => Object.fromEntries(spec.map((f) => [f.key, '']))}
      onChange={onChange}
      columns={1}
      addLabel="Add row"
      emptyHint="Nothing here yet — this section stays hidden until you add a row."
      // Replicated-page images are served by their own public route; the
      // storefront's would 404 on these keys.
      uploadImage={uploadReplicatedImage}
      imageUrl={replicatedImageUrl}
    />
  );
}

// ── Dispatch ────────────────────────────────────────────────────────────────

/** Render one field spec against a section's `config`. */
export function RpFieldControl({
  spec,
  config,
  onChange,
}: {
  spec: RpFieldSpec;
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const v = config[spec.key];
  const set = (value: unknown) => onChange(spec.key, value);

  switch (spec.kind) {
    case 'text':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <Input
            className="h-8"
            value={typeof v === 'string' ? v : ''}
            placeholder={spec.placeholder}
            onChange={(e) => set(e.target.value)}
          />
        </RpFieldRow>
      );
    case 'textarea':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <Textarea
            rows={3}
            value={typeof v === 'string' ? v : ''}
            placeholder={spec.placeholder}
            onChange={(e) => set(e.target.value)}
          />
        </RpFieldRow>
      );
    case 'richtext':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          {/* Stored srcs are origin-relative so they survive a domain change;
              the editor sees them absolutized and we relativize on every edit. */}
          <RichTextEditor
            variant="full"
            value={absolutizeReplicatedImageSrcs(typeof v === 'string' ? v : '', API_BASE_URL)}
            onChange={(html) => set(relativizeReplicatedImageSrcs(html, API_BASE_URL))}
            placeholder="Write here…"
            onUploadImage={async (file) => {
              const res = await uploadReplicatedImage(file);
              return `${API_BASE_URL}${REPLICATED_IMAGE_PATH}?key=${encodeURIComponent(res.key)}`;
            }}
          />
        </RpFieldRow>
      );
    case 'number':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <Input
            className="h-8 w-28"
            type="number"
            min={spec.min}
            max={spec.max}
            value={typeof v === 'number' ? String(v) : ''}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n))
                set(Math.min(spec.max ?? n, Math.max(spec.min ?? n, Math.round(n))));
            }}
          />
        </RpFieldRow>
      );
    case 'toggle':
      return (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={v === true} onCheckedChange={(c) => set(c === true)} />
          {spec.label}
        </label>
      );
    case 'segmented':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <SegmentedControl
            size="sm"
            value={(v as string | number) ?? spec.options[0]?.value}
            onChange={set}
            options={spec.options}
            ariaLabel={spec.label}
          />
        </RpFieldRow>
      );
    case 'imageKey':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <RpImageKeyField value={typeof v === 'string' ? v : ''} onChange={set} />
        </RpFieldRow>
      );
    case 'media':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <RpMediaField value={v as RpMediaRef | undefined} onChange={set} />
        </RpFieldRow>
      );
    case 'button':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <RpButtonField
            value={v as RpButton | undefined}
            optional={spec.optional}
            onChange={set}
          />
        </RpFieldRow>
      );
    case 'strings':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <RpStringsField
            value={Array.isArray(v) ? (v as string[]) : []}
            max={spec.max}
            placeholder={spec.placeholder}
            onChange={set}
          />
        </RpFieldRow>
      );
    case 'items':
      return (
        <RpFieldRow label={spec.label} hint={spec.hint}>
          <RpItemsField
            value={Array.isArray(v) ? (v as Record<string, unknown>[]) : []}
            spec={spec.itemFields}
            onChange={(next) => set(next.slice(0, spec.max))}
          />
        </RpFieldRow>
      );
    default:
      return null;
  }
}
